import { useQuery } from '@apollo/client'
import { Box, HeaderText, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { useOnNext } from '@factorialco/deprecated-design-system'
import { GetCurrenciesDocument } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getAssetTypeSelectOptions } from 'modules/it_management/lib/constants'
import { isValidPriceFormat } from 'modules/it_management/lib/validationUtils'

import { WizardData } from './types'

const DetailsStep: React.FC<WizardStepRenderParams<WizardData>> = ({ data, onDataChanged }) => {
  const { data: currenciesData } = useQuery(GetCurrenciesDocument)
  const { company, employee } = useCurrentSession()

  useOnNext(() => {
    track(trackingEvents.itManagement.newDevice.createEquipmentAssignation)
  })

  const assetTypeOptions = getAssetTypeSelectOptions()

  const currencyOptions = currenciesData?.apiCore.currenciesConnection.edges.map((edge) => ({
    value: edge.node.isoCode,
    label: `${edge.node.symbol} ${edge.node.isoCode}`,
  })) || [
    { value: 'EUR', label: '€ EUR' },
    { value: 'USD', label: '$ USD' },
    { value: 'GBP', label: '£ GBP' },
  ]

  const userCurrency = employee?.legalEntity?.currency || company.currency
  const defaultCurrency =
    currencyOptions.find((option) => option.value === userCurrency)?.value || 'EUR'

  const today = new Date()
  const purchaseDate = data.purchaseDate
    ? stringToDate(data.purchaseDate, 'backend').toDate()
    : null
  const warrantyMinDate = purchaseDate || today

  return (
    <Box gap="s32" maxWidth="s400">
      <Box gap="s16" paddingRight="s16">
        <HeaderText text={i18n.t('it_management.new_equipment_modal.sections.mandatory_fields')} />
        <SelectField
          mandatory
          value={data.assetModelType}
          label={i18n.t('it_management.new_equipment_modal.fields.type_of_equipment')}
          missingLabel={i18n.t('it_management.new_equipment_modal.placeholders.select_type')}
          options={assetTypeOptions}
          onChange={(value: string) => onDataChanged({ ...data, assetModelType: value })}
        />
        <Box flexDirection="row" gap="s16">
          <TextField
            mandatory
            value={data.assetModelBrand || ''}
            label={i18n.t('it_management.new_equipment_modal.fields.manufacturer')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.brand_name')}
            onChange={(value: string) => onDataChanged({ ...data, assetModelBrand: value })}
          />

          <TextField
            mandatory
            value={data.assetModelName || ''}
            label={i18n.t('it_management.new_equipment_modal.fields.model_name')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.model_name')}
            onChange={(value: string) => onDataChanged({ ...data, assetModelName: value })}
          />
        </Box>
      </Box>

      <Box gap="s16" paddingRight="s16">
        <HeaderText text={i18n.t('it_management.new_equipment_modal.sections.equipment_details')} />
        <Box flexDirection="row" gap="s16">
          <TextField
            value={data.serialNumber || ''}
            label={i18n.t('it_management.common.serial_number')}
            onChange={(value: string) => onDataChanged({ ...data, serialNumber: value })}
          />
        </Box>
        <Box flexDirection="row" gap="s16">
          <TextField
            value={data.label || ''}
            label={i18n.t('it_management.common.internal_label')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.internal_label')}
            description={i18n.t(
              'it_management.new_equipment_modal.fields.internal_label_description'
            )}
            onChange={(value: string) => onDataChanged({ ...data, label: value })}
          />
        </Box>
        <Box flexDirection="row" gap="s16">
          <TextArea
            value={data.notes || ''}
            label={i18n.t('it_management.common.notes')}
            onChange={(value: string) => onDataChanged({ ...data, notes: value })}
          />
        </Box>
      </Box>

      <Box gap="s16" paddingRight="s16">
        <HeaderText
          text={i18n.t('it_management.new_equipment_modal.sections.finance_information')}
        />
        <Box flexDirection="row" gap="s16">
          <DayPicker
            value={data.purchaseDate || ''}
            label={i18n.t('it_management.new_equipment_modal.fields.purchase_date')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.purchase_date')}
            maxDate={today}
            onChange={(value: string) => onDataChanged({ ...data, purchaseDate: value })}
            minDate={new Date(new Date().getFullYear() - 10, 0, 1)}
            valueFormat="backend"
          />

          <DayPicker
            value={data.warrantyEndDate || ''}
            label={i18n.t('it_management.new_equipment_modal.fields.warranty_end_date')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.warranty_end_date')}
            minDate={warrantyMinDate}
            onChange={(value: string) => onDataChanged({ ...data, warrantyEndDate: value })}
            maxDate={new Date(new Date().getFullYear() + 10, 11, 31)}
            valueFormat="backend"
          />
        </Box>
        <Box flexDirection="row" gap="s16">
          <TextField
            value={data.purchasePrice || ''}
            label={i18n.t('it_management.new_equipment_modal.fields.purchase_price')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.purchase_price')}
            type="number"
            min={0}
            onChange={(value: string) => {
              if (isValidPriceFormat(value)) {
                onDataChanged({ ...data, purchasePrice: value })
              }
            }}
          />
          <SelectField
            value={data.currency || defaultCurrency}
            label={i18n.t('it_management.new_equipment_modal.fields.currency')}
            missingLabel={i18n.t('it_management.new_equipment_modal.placeholders.select_currency')}
            options={currencyOptions}
            onChange={(value: string) => onDataChanged({ ...data, currency: value })}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default DetailsStep
